import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import { db } from "integrations/firebase/firestore";

/**
 * The real attendee records, not the PII-free directory projection.
 *
 * Only event admins may list this collection — it carries email addresses —
 * which is exactly why the admin screen reads it here rather than reusing the
 * participant-facing directory hook.
 */
export const attendeeRecordSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().optional(),
  role: z.string().optional(),
  company: z.string().optional(),
  attendeeType: z.string().optional(),
  status: z.string().optional(),
  bio: z.string().optional(),
  linkedinUrl: z.string().optional(),
  avatar: z.string().optional(),
  trackId: z.string().nullable().optional(),
  uid: z.string().nullable().optional(),
  createdAt: z.any().optional(),
});
export type AttendeeRecord = z.infer<typeof attendeeRecordSchema>;

export type AttendeeRecordsState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; data: AttendeeRecord[] };

export function useAttendeeRecords(eventId: string): AttendeeRecordsState & {
  reload: () => void;
} {
  const [state, setState] = useState<AttendeeRecordsState>({
    status: "loading",
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    getDocs(
      query(collection(db, "events", eventId, "attendees"), orderBy("name")),
    )
      .then((snap) => {
        if (cancelled) return;
        const data: AttendeeRecord[] = [];
        snap.forEach((d) => {
          const result = attendeeRecordSchema.safeParse({
            id: d.id,
            ...d.data(),
          });
          if (result.success) data.push(result.data);
        });
        setState({ status: "success", data });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          error:
            err instanceof Error ? err.message : "Failed to load attendees",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [eventId, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  return { ...state, reload };
}
